import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  WhatsappLogo, ChatsCircle, Robot, Users, Lightning, Clock, Kanban, PlugsConnected,
  ChartLineUp, ShieldCheck, ArrowRight, Check, Question, Rocket, CheckCircle, LockKey,
  InstagramLogo, FacebookLogo, YoutubeLogo, LinkedinLogo, TiktokLogo, XLogo, X, Copy,
  Envelope, Phone, MapPin,
} from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const RESELLER_ATTR_KEY = "zm_reseller_slug";

const readAttribution = () => {
  try { return localStorage.getItem(RESELLER_ATTR_KEY) || ""; } catch { return ""; }
};

const FEATURES = [
  { icon: PlugsConnected, title: "Multi-Conexões", desc: "Conecte vários números WhatsApp em uma única plataforma sem perder o controle." },
  { icon: Robot, title: "IA Inteligente", desc: "Auto-respostas com Claude 4.6 e transferência para humano quando o cliente pedir." },
  { icon: Kanban, title: "Kanban Visual", desc: "Aguardando, Em atendimento e Resolvidos — visão instantânea da fila." },
  { icon: Users, title: "Equipe & Setores", desc: "Distribua atendimentos por setor, transfira tickets, controle carga de cada atendente." },
  { icon: Clock, title: "Horário & SLA", desc: "Fora do horário? Resposta automática. Meta de SLA por ticket com contador visível." },
  { icon: ChatsCircle, title: "Chat Multimídia", desc: "Áudio, imagem, vídeo, documento e figurinha — envie e receba direto do painel." },
  { icon: Lightning, title: "Respostas Rápidas", desc: "Templates com variáveis e atalhos para responder em 1 clique." },
  { icon: ChartLineUp, title: "Métricas em Tempo Real", desc: "Volume, tempo médio de resposta, satisfação e conversão em um painel único." },
  { icon: ShieldCheck, title: "Segurança total", desc: "JWT, dados isolados por empresa, backups automáticos e assinatura HMAC nos webhooks." },
];

const HOW_STEPS = [
  { icon: Rocket, title: "1. Escolha um plano", desc: "Selecione o plano que combina com o volume de atendimento da sua empresa." },
  { icon: WhatsappLogo, title: "2. Pague via Pix", desc: "Pagamento instantâneo pelo Pix. Sua conta é liberada em segundos." },
  { icon: PlugsConnected, title: "3. Conecte seu WhatsApp", desc: "Escaneie o QR Code como no WhatsApp Web e comece a receber mensagens." },
  { icon: ChartLineUp, title: "4. Escale sem perder cliente", desc: "IA responde 24/7, Kanban organiza a fila e métricas mostram o que está performando." },
];

const FAQ = [
  { q: "Preciso instalar alguma coisa no meu WhatsApp?", a: "Não. Você conecta escaneando um QR Code como se fosse o WhatsApp Web. Em 30 segundos você já está atendendo pela plataforma." },
  { q: "Posso ter vários atendentes no mesmo número?", a: "Sim! Vários atendentes acessam o mesmo número simultaneamente, cada um com seu login. O sistema distribui automaticamente e permite transferência entre eles." },
  { q: "Meu número corre risco de bloqueio?", a: "Não, desde que você siga as diretrizes do WhatsApp. Nosso sistema usa conexão receptiva (igual WhatsApp Web) e não faz envios massivos automáticos sem sua permissão." },
  { q: "Como funciona a comissão de revendedor?", a: "Cada revendedor recebe uma URL pública personalizada (com logo e cor da marca) e ganha 40% de comissão sobre cada cadastro feito através do link dele." },
  { q: "Existe fidelidade ou taxa de cancelamento?", a: "Nenhuma. Você paga só a mensalidade e pode cancelar quando quiser. Sem letras miúdas, sem multas." },
  { q: "E se eu não gostar da plataforma?", a: "Você tem 7 dias de garantia. Se por qualquer motivo não gostar do sistema nos primeiros 7 dias após assinar, é só entrar em contato e a gente devolve 100% do valor pago — sem burocracia e sem pergunta." },
  { q: "Como funciona o pagamento?", a: "Você paga uma mensalidade via Pix. Simples e transparente, sem cartão de crédito nem contratos longos." },
];

export default function Home() {
  const [plans, setPlans] = useState([]);
  const [site, setSite] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [signupPlanId, setSignupPlanId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([
          axios.get(`${API}/plans/public`),
          axios.get(`${API}/site-settings`),
        ]);
        setPlans(p.data);
        setSite(s.data || {});
      } catch (e) { console.warn("[home] could not load plans/site-settings:", e?.message || e); }
    })();
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const openSignup = (planId = null) => {
    // If reseller attribution exists, redirect to reseller landing (their commission)
    const slug = readAttribution();
    if (slug) {
      navigate(`/r/${slug}`);
      return;
    }
    setSignupPlanId(planId || (plans[0]?.id ?? null));
    setShowSignup(true);
  };

  return (
    <div className="min-h-screen bg-white text-[#0F1614]">
      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 bg-[#0F4C3A] flex items-center justify-center">
              <WhatsappLogo size={22} weight="fill" className="text-white" />
            </div>
            <div className="font-black text-xl tracking-tighter">NETS<span className="text-[#0F4C3A]">ZAP</span></div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold">
            <button onClick={() => scrollTo("recursos")} className="hover:text-[#0F4C3A] transition-colors" data-testid="nav-recursos">Recursos</button>
            <button onClick={() => scrollTo("como-funciona")} className="hover:text-[#0F4C3A] transition-colors" data-testid="nav-como-funciona">Como funciona</button>
            <button onClick={() => scrollTo("planos")} className="hover:text-[#0F4C3A] transition-colors" data-testid="nav-planos">Planos</button>
            <button onClick={() => scrollTo("faq")} className="hover:text-[#0F4C3A] transition-colors" data-testid="nav-faq">FAQ</button>
            <button onClick={() => scrollTo("contato")} className="hover:text-[#0F4C3A] transition-colors" data-testid="nav-contato">Contato</button>
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/login")}
              className="text-xs font-semibold text-[#5C5C59] hover:text-[#0F4C3A] transition-colors hidden sm:inline"
              data-testid="header-login-link"
            >
              Entrar
            </button>
            <Button
              onClick={() => openSignup()}
              data-testid="header-signup-btn"
              className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white font-bold"
            >
              Assinar agora
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-[#F7FBF9] to-[#EDF3F0]">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A] mb-4">Plataforma de multi-atendimento</p>
            <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-[1.02]">
              Transforme seu WhatsApp em uma <span className="text-[#0F4C3A]">máquina de vendas</span>.
            </h1>
            <p className="text-lg text-[#5C5C59] mt-6 leading-relaxed">
              Um único número, dezenas de atendentes, IA que responde 24/7 e Kanban visual pra nunca perder um cliente. Configure em 3 minutos.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => openSignup()} data-testid="hero-cta-signup" size="lg" className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white text-base px-8 py-6">
                Assinar agora <ArrowRight size={18} weight="bold" className="ml-2" />
              </Button>
              <Button onClick={() => scrollTo("planos")} data-testid="hero-cta-planos" variant="outline" size="lg" className="rounded-none border-[#0F4C3A] text-[#0F4C3A] text-base px-8 py-6">
                Ver planos
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-6 mt-10 text-xs text-[#5C5C59]">
              <span className="flex items-center gap-1.5"><Check size={14} weight="bold" className="text-[#0F4C3A]" /> Pagamento via Pix</span>
              <span className="flex items-center gap-1.5"><Check size={14} weight="bold" className="text-[#0F4C3A]" /> Sem fidelidade</span>
              <span className="flex items-center gap-1.5"><Check size={14} weight="bold" className="text-[#0F4C3A]" /> Suporte em português</span>
            </div>
          </div>
          <div className="relative">
            <div className="bg-white border-2 border-[#0F4C3A] shadow-brutal p-6 relative">
              <div className="flex items-center gap-2 pb-3 border-b border-[#E5E5E0]">
                <div className="h-2.5 w-2.5 bg-red-500 rounded-full" />
                <div className="h-2.5 w-2.5 bg-yellow-400 rounded-full" />
                <div className="h-2.5 w-2.5 bg-green-500 rounded-full" />
                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#5C5C59]">painel.netszap</span>
              </div>
              <div className="mt-4 space-y-2.5">
                {[
                  { name: "Maria S.", msg: "Olá, quero fazer meu pedido!", time: "17:02", tag: "Aguardando", tagColor: "#D97706" },
                  { name: "Pedro R.", msg: "Já foi pago, quando chega?", time: "17:05", tag: "Em atendimento", tagColor: "#2563EB" },
                  { name: "Ana C.", msg: "Muito obrigada pelo atendimento! ❤️", time: "17:10", tag: "Resolvido", tagColor: "#0F4C3A" },
                ].map((t) => (
                  <div key={t.name} className="p-3 bg-[#F7F7F5] border-l-[3px]" style={{ borderColor: t.tagColor }}>
                    <div className="flex items-center justify-between text-xs">
                      <b>{t.name}</b>
                      <span className="text-[10px] text-[#5C5C59] font-mono">{t.time}</span>
                    </div>
                    <div className="text-sm text-[#5C5C59] truncate mt-0.5">{t.msg}</div>
                    <span className="inline-block text-[9px] uppercase tracking-wider font-bold mt-1 px-1.5 py-0.5" style={{ background: t.tagColor + "20", color: t.tagColor }}>{t.tag}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="bg-[#0F4C3A] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-white/70 text-center mb-3">Como funciona</p>
          <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12 tracking-tighter">Em 4 passos você tá <span className="text-[#7FD8B8]">atendendo profissional</span>.</h2>
          <div className="grid md:grid-cols-4 gap-5">
            {HOW_STEPS.map((s, idx) => (
              <div key={s.title} className="bg-white p-6 border-2 border-transparent hover:border-white transition-all" data-testid={`step-${idx}`}>
                <div className="h-12 w-12 bg-[#EDF3F0] flex items-center justify-center">
                  <s.icon size={26} weight="duotone" className="text-[#0F4C3A]" />
                </div>
                <h3 className="text-lg font-black mt-4">{s.title}</h3>
                <p className="text-sm text-[#5C5C59] mt-1.5 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button onClick={() => openSignup()} data-testid="how-cta-signup" size="lg" className="rounded-none bg-white hover:bg-[#F7F7F5] text-[#0F4C3A] font-bold text-base px-8 py-6">
              Começar agora <ArrowRight size={18} weight="bold" className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* RECURSOS */}
      <section id="recursos" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A] text-center">Recursos</p>
          <h2 className="text-3xl md:text-5xl font-black text-center mt-2 tracking-tighter">Tudo que você precisa em <span className="text-[#0F4C3A]">um só lugar</span>.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-[#E5E5E0] p-6 hover:border-[#0F4C3A] hover:shadow-brutal transition-all">
                <div className="h-11 w-11 bg-[#EDF3F0] flex items-center justify-center">
                  <f.icon size={22} weight="duotone" className="text-[#0F4C3A]" />
                </div>
                <h3 className="text-lg font-black mt-4">{f.title}</h3>
                <p className="text-sm text-[#5C5C59] mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section id="planos" className="py-20 bg-[#F7F7F5]">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A] text-center">Planos</p>
          <h2 className="text-3xl md:text-5xl font-black text-center mt-2 tracking-tighter">Escolha o plano que <span className="text-[#0F4C3A]">cabe no seu bolso</span>.</h2>
          <p className="text-center text-[#5C5C59] mt-3">Mensalidade via Pix. Sem cartão de crédito, sem fidelidade.</p>
          <div className={`grid gap-5 mt-12 ${plans.length >= 3 ? "md:grid-cols-3" : plans.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1 max-w-md mx-auto"}`}>
            {plans.map((p, idx) => {
              const highlight = plans.length >= 3 && idx === 2;
              return (
                <div key={p.id} className={`bg-white p-6 border-2 relative flex flex-col ${highlight ? "border-[#0F4C3A] shadow-brutal" : "border-[#E5E5E0]"}`}>
                  {highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D97706] text-white text-[10px] uppercase font-bold px-3 py-1 tracking-wider">Mais vendido</span>
                  )}
                  <h3 className="text-2xl font-black">{p.name}</h3>
                  <p className="text-4xl font-black tracking-tighter my-3 text-[#0F4C3A]">
                    R$ {p.price.toFixed(2).replace(".", ",")}<span className="text-sm text-[#5C5C59] font-normal">/mês</span>
                  </p>
                  <ul className="space-y-1.5 text-sm mt-2 flex-1">
                    <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-[#0F4C3A]" /> {p.max_attendants >= 999 ? "Atendentes ilimitados" : `${p.max_attendants} atendentes`}</li>
                    <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-[#0F4C3A]" /> {p.max_connections} {p.max_connections === 1 ? "conexão" : "conexões"} WhatsApp</li>
                    {(p.features || []).map((f, i) => (
                      <li key={`${p.id}-${i}`} className="flex items-start gap-2"><Check size={14} weight="bold" className="text-[#0F4C3A] mt-0.5 flex-shrink-0" /> <span>{f}</span></li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-6 rounded-none ${highlight ? "bg-[#0F4C3A] hover:bg-[#0A3629] text-white" : "bg-white border border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#EDF3F0]"}`}
                    onClick={() => openSignup(p.id)}
                    data-testid={`plan-cta-${p.id}`}
                  >
                    Assinar este plano
                  </Button>
                </div>
              );
            })}
            {plans.length === 0 && <p className="text-center text-[#5C5C59] col-span-3 py-6">Carregando planos…</p>}
          </div>

          {/* 7-day guarantee */}
          <div className="mt-14 max-w-3xl mx-auto" data-testid="guarantee-badge">
            <div className="bg-white border-2 border-[#0F4C3A] p-6 md:p-8 flex items-start md:items-center gap-5 shadow-brutal">
              <div className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 bg-[#0F4C3A] flex items-center justify-center rounded-full">
                <ShieldCheck size={32} weight="fill" className="text-white" />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#D97706]">Garantia incondicional</p>
                <h3 className="text-xl md:text-2xl font-black tracking-tight mt-1">7 dias de garantia — dinheiro de volta</h3>
                <p className="text-sm md:text-base text-[#5C5C59] mt-2 leading-relaxed">
                  Assine sem medo. Se por qualquer motivo você não gostar da plataforma nos primeiros 7 dias, é só pedir e a gente devolve <b className="text-[#0F4C3A]">100% do valor pago</b>, sem burocracia e sem pergunta.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A] text-center">Dúvidas frequentes</p>
          <h2 className="text-3xl md:text-4xl font-black text-center mt-2 tracking-tighter">Ainda tem <span className="text-[#0F4C3A]">dúvidas</span>?</h2>
          <div className="space-y-2 mt-10">
            {FAQ.map((f, i) => (
              <div key={f.q} className="border border-[#E5E5E0] bg-white">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`faq-${i}`} className="w-full text-left p-4 flex items-center justify-between hover:bg-[#F7F7F5] transition-colors">
                  <span className="font-semibold pr-4">{f.q}</span>
                  <Question size={18} weight={openFaq === i ? "fill" : "regular"} className="text-[#0F4C3A] flex-shrink-0" />
                </button>
                {openFaq === i && <div className="px-4 pb-4 text-sm text-[#5C5C59] leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer site={site} onSignup={() => openSignup()} />

      {showSignup && (
        <SignupModal
          plans={plans}
          initialPlanId={signupPlanId}
          onClose={() => setShowSignup(false)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// FOOTER
// -----------------------------------------------------------------------------
function Footer({ site, onSignup }) {
  const socials = [
    { url: site.instagram_url, icon: InstagramLogo, label: "Instagram" },
    { url: site.facebook_url, icon: FacebookLogo, label: "Facebook" },
    { url: site.twitter_url, icon: XLogo, label: "Twitter" },
    { url: site.youtube_url, icon: YoutubeLogo, label: "YouTube" },
    { url: site.linkedin_url, icon: LinkedinLogo, label: "LinkedIn" },
    { url: site.tiktok_url, icon: TiktokLogo, label: "TikTok" },
  ].filter((s) => s.url);

  const hasContact = site.email || site.phone || site.whatsapp;
  const hasCompany = site.company_legal_name || site.cnpj;

  return (
    <footer id="contato" className="relative bg-[#0A1210] text-white" data-testid="site-footer">
      {/* Accent top bar */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#22A882] to-transparent" />

      <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* --- Brand column --- */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 bg-[#0F4C3A] flex items-center justify-center shadow-brutal">
                <WhatsappLogo size={24} weight="fill" className="text-white" />
              </div>
              <div>
                <div className="font-black text-2xl tracking-tighter">
                  NETS<span className="text-[#7FD8B8]">ZAP</span>
                </div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-0.5">
                  WhatsApp business platform
                </p>
              </div>
            </div>

            <p className="text-sm text-white/60 mt-5 max-w-md leading-relaxed">
              {site.footer_tagline || "Plataforma de multi-atendimento WhatsApp com IA, Kanban e gestão de equipe."}
            </p>

            <Button
              onClick={onSignup}
              data-testid="footer-signup-btn"
              className="mt-6 rounded-none bg-[#0F4C3A] hover:bg-[#22A882] hover:text-[#0A1210] text-white font-bold transition-colors"
            >
              Assinar agora <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>

            {socials.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-widest font-bold text-white/40 mt-8 mb-3">Siga a gente</p>
                <div className="flex items-center gap-2" data-testid="footer-socials">
                  {socials.map((s) => (
                    <a
                      key={s.label}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-social-${s.label.toLowerCase()}`}
                      className="group h-10 w-10 border border-white/15 flex items-center justify-center hover:bg-[#22A882] hover:border-[#22A882] transition-all hover:-translate-y-0.5"
                      aria-label={s.label}
                    >
                      <s.icon size={18} weight="fill" className="text-white/70 group-hover:text-[#0A1210] transition-colors" />
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* --- Contact column --- */}
          <div className="lg:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#7FD8B8] mb-5 flex items-center gap-2">
              <span className="h-px w-6 bg-[#7FD8B8]/50" /> Contato
            </p>
            {hasCompany && (
              <div className="mb-5 pb-5 border-b border-white/5">
                {site.company_legal_name && (
                  <div className="text-sm font-semibold text-white">{site.company_legal_name}</div>
                )}
                {site.cnpj && (
                  <div className="text-xs text-white/50 font-mono mt-1">CNPJ {site.cnpj}</div>
                )}
              </div>
            )}
            {hasContact ? (
              <ul className="space-y-3">
                {site.email && (
                  <li>
                    <a href={`mailto:${site.email}`} className="flex items-start gap-3 group" data-testid="footer-email">
                      <Envelope size={16} weight="duotone" className="text-[#7FD8B8] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-white/80 group-hover:text-[#7FD8B8] transition-colors break-all">{site.email}</span>
                    </a>
                  </li>
                )}
                {site.phone && (
                  <li className="flex items-start gap-3">
                    <Phone size={16} weight="duotone" className="text-[#7FD8B8] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-white/80 font-mono">{site.phone}</span>
                  </li>
                )}
                {site.whatsapp && (
                  <li>
                    <a
                      href={`https://wa.me/${site.whatsapp.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 group"
                      data-testid="footer-whatsapp"
                    >
                      <WhatsappLogo size={16} weight="fill" className="text-[#25D366] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-white/80 font-mono group-hover:text-[#25D366] transition-colors">{site.whatsapp}</span>
                    </a>
                  </li>
                )}
              </ul>
            ) : (
              <p className="text-xs text-white/30 italic">Configure em Admin › Site</p>
            )}
          </div>

          {/* --- Address column --- */}
          <div className="lg:col-span-3">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#7FD8B8] mb-5 flex items-center gap-2">
              <span className="h-px w-6 bg-[#7FD8B8]/50" /> Endereço
            </p>
            {site.address ? (
              <div className="flex items-start gap-3">
                <MapPin size={16} weight="duotone" className="text-[#7FD8B8] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-white/80 whitespace-pre-line leading-relaxed">{site.address}</p>
              </div>
            ) : (
              <p className="text-xs text-white/30 italic">Configure em Admin › Site</p>
            )}

            {/* Quick links */}
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#7FD8B8] mt-8 mb-4 flex items-center gap-2">
              <span className="h-px w-6 bg-[#7FD8B8]/50" /> Navegação
            </p>
            <ul className="space-y-2 text-sm">
              <li><a href="#planos" className="text-white/60 hover:text-white transition-colors">Planos</a></li>
              <li><a href="#recursos" className="text-white/60 hover:text-white transition-colors">Recursos</a></li>
              <li><a href="#faq" className="text-white/60 hover:text-white transition-colors">Perguntas frequentes</a></li>
              <li><a href="/login" className="text-white/60 hover:text-white transition-colors">Entrar no painel</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} {site.company_legal_name || "NetsZap"} — Todos os direitos reservados.</span>
          <span className="flex items-center gap-2">
            Feito no Brasil <span className="text-sm">🇧🇷</span>
          </span>
        </div>
      </div>
    </footer>
  );
}

// -----------------------------------------------------------------------------
// SIGNUP MODAL — paid Pix flow, direct to Master
// -----------------------------------------------------------------------------
function SignupModal({ plans, initialPlanId, onClose }) {
  const [planId, setPlanId] = useState(initialPlanId || plans[0]?.id || "");
  const [form, setForm] = useState({
    company_name: "", owner_name: "", owner_email: "", owner_password: "",
    owner_cpf: "", owner_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Poll for payment approval
  useEffect(() => {
    if (!checkout?.purchase_id || checkout.status === "approved") return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/public/master-signup/${checkout.purchase_id}`);
        setCheckout((prev) => ({ ...prev, ...data }));
        if (data.status === "approved") {
          toast.success("Pagamento aprovado! Sua conta foi criada.");
          clearInterval(t);
        }
      } catch (e) { console.debug("[home] signup checkout poll (retry):", e?.message || e); }
    }, 5000);
    return () => clearInterval(t);
  }, [checkout?.purchase_id, checkout?.status]);

  const submit = async () => {
    if (!planId) return toast.error("Escolha um plano");
    if (!form.company_name || !form.owner_name || !form.owner_email || !form.owner_password || !form.owner_cpf) {
      return toast.error("Preencha todos os campos obrigatórios");
    }
    if (form.owner_password.length < 6) return toast.error("Senha deve ter pelo menos 6 caracteres");
    setSubmitting(true);
    try {
      const payload = {
        plan_id: planId,
        ...form,
        owner_cpf: form.owner_cpf.replace(/\D/g, ""),
      };
      const { data } = await axios.post(`${API}/public/signup`, payload);
      setCheckout(data);
      toast.success("Pix gerado. Escaneie no seu banco.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar Pix");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = useCallback(() => {
    navigator.clipboard.writeText(checkout?.qr_code || "");
    setCopied(true);
    toast.success("Código Pix copiado");
    setTimeout(() => setCopied(false), 2000);
  }, [checkout?.qr_code]);

  const selectedPlan = plans.find((p) => p.id === planId);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="signup-modal">
      <div className="bg-white border-2 border-[#0F4C3A] shadow-brutal max-w-2xl w-full my-8">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E0]">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Assinatura mensal</p>
            <h2 className="text-2xl font-black tracking-tighter mt-1">Crie sua conta NetsZap</h2>
          </div>
          <button onClick={onClose} data-testid="signup-modal-close" className="p-2 hover:bg-[#F7F7F5]" aria-label="Fechar">
            <X size={20} weight="bold" />
          </button>
        </div>

        {checkout?.status === "approved" ? (
          <div className="p-8 text-center">
            <CheckCircle size={56} weight="fill" className="text-[#0F4C3A] mx-auto" />
            <h3 className="text-2xl font-black mt-4">Pagamento aprovado!</h3>
            <p className="text-sm text-[#5C5C59] mt-2">
              Sua conta foi criada no plano <b>{checkout.plan_name}</b>.
            </p>
            <div className="mt-6 p-4 bg-[#F7F7F5] border-l-[3px] border-[#0F4C3A] text-left">
              <p className="text-xs text-[#5C5C59]">Entre com:</p>
              <p className="font-mono text-sm mt-1"><b>E-mail:</b> {form.owner_email}</p>
            </div>
            <Button onClick={() => (window.location.href = "/login")} data-testid="signup-goto-login" className="mt-6 rounded-none w-full bg-[#0F4C3A] hover:bg-[#0A3629] text-white py-6">
              Entrar no painel <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>
          </div>
        ) : checkout ? (
          <div className="p-6" data-testid="signup-pix-result">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#5C5C59]">Pague com Pix</p>
            <p className="text-2xl font-black mt-1">R$ {checkout.amount_brl.toFixed(2).replace(".", ",")} — {checkout.plan_name}</p>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-[#5C5C59]">Escaneie no seu banco</p>
                {checkout.qr_code_base64 && (
                  <img src={`data:image/png;base64,${checkout.qr_code_base64}`} alt="Pix QR" className="w-full max-w-[280px] border mt-2" />
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-[#5C5C59]">Ou copie o código</p>
                <div className="mt-2 p-3 bg-[#F7F7F5] border border-[#E5E5E0] font-mono text-[11px] break-all max-h-32 overflow-y-auto">
                  {checkout.qr_code}
                </div>
                <Button onClick={copyCode} className="rounded-none w-full mt-2 bg-[#0F4C3A] hover:bg-[#0A3629] text-white" data-testid="signup-pix-copy">
                  <Copy size={14} weight="bold" className="mr-2" /> {copied ? "Copiado!" : "Copiar código"}
                </Button>
                <div className="mt-3 p-3 bg-[#FEF3C7] border-l-[3px] border-[#D97706] text-xs">
                  Após pagar, sua conta é criada automaticamente. Esta janela atualiza a cada 5s.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#5C5C59] mb-2">1. Plano</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
              {plans.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`signup-plan-${p.id}`}
                  onClick={() => setPlanId(p.id)}
                  className={`text-left p-3 border-2 transition-all ${planId === p.id ? "border-[#0F4C3A] bg-[#EDF3F0]" : "border-[#E5E5E0] hover:border-[#C7C7C0] bg-white"}`}
                >
                  <p className="font-black text-sm">{p.name}</p>
                  <p className="text-lg font-black text-[#0F4C3A]">R$ {p.price.toFixed(2).replace(".", ",")}<span className="text-xs text-[#5C5C59] font-normal">/mês</span></p>
                  <p className="text-[10px] text-[#5C5C59]">{p.max_attendants} atend · {p.max_connections} conex</p>
                </button>
              ))}
            </div>

            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#5C5C59] mb-2">2. Seus dados</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase font-semibold tracking-wider">Empresa</Label>
                <Input data-testid="signup-company-input" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold tracking-wider">Seu nome</Label>
                <Input data-testid="signup-name-input" value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold tracking-wider">E-mail</Label>
                <Input data-testid="signup-email-input" type="email" value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold tracking-wider">Senha (mín. 6)</Label>
                <Input data-testid="signup-password-input" type="password" value={form.owner_password} onChange={(e) => set("owner_password", e.target.value)} className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold tracking-wider">CPF (para o Pix)</Label>
                <Input data-testid="signup-cpf-input" value={form.owner_cpf} onChange={(e) => set("owner_cpf", e.target.value)} className="rounded-none mt-1 font-mono" placeholder="000.000.000-00" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold tracking-wider">WhatsApp (opcional)</Label>
                <Input data-testid="signup-phone-input" value={form.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} className="rounded-none mt-1 font-mono" placeholder="(11) 99999-9999" />
              </div>
            </div>

            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="signup-submit-btn"
              className="mt-6 rounded-none w-full bg-[#0F4C3A] hover:bg-[#0A3629] text-white py-6 text-base"
            >
              {submitting ? "Gerando Pix…" : `Pagar R$ ${(selectedPlan?.price || 0).toFixed(2).replace(".", ",")} via Pix`}
            </Button>
            <p className="text-[10px] text-[#5C5C59] mt-3 text-center">
              Pagamento seguro via Mercado Pago. Sua conta é liberada assim que o Pix for confirmado.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
